/**
 * Persian → English domain glossary, for query expansion.
 *
 * ## The problem it solves
 *
 * The library is written in English. V13 established why and closed the question: Persian AI
 * communities publish English prompts with Persian commentary, so a "Farsi prompt library" is not
 * a real thing to build. But that leaves a Persian *speaker* stuck — a real user searched
 * `سایبرپانک` and got nothing while 542 cyberpunk prompts sat in the library.
 *
 * Folding (see `persian.ts`) makes identical-looking Persian compare equal. It cannot make Persian
 * find English. This can, for the few hundred words that actually matter in this domain.
 *
 * ## Expansion, never replacement
 *
 * A Persian query is searched as Persian **and** as its English equivalents. Replacing would throw
 * away the user's own words, which is wrong whenever a prompt genuinely does contain Persian — and
 * 82 of them do.
 *
 * ## Why a table and not a translation API
 *
 * A translation call per keystroke costs money, latency and a quota, and returns fluent prose
 * where a search wants terms. Two hundred curated pairs cover the domain vocabulary — styles,
 * subjects, media, models — because prompt search is a small, closed world. Terms are stored as
 * folded keys so a lookup is one map hit with no per-query normalisation of the table.
 */

import { foldPersian } from './persian';

/**
 * Persian term → English equivalents.
 *
 * Curated for *this* library: the vocabulary of image, video and text prompting. Several entries
 * map to more than one English word because Persian collapses distinctions English makes (منظره
 * is both landscape and scenery) — and searching both is exactly the point.
 */
const PAIRS: Record<string, string[]> = {
  // ── styles and aesthetics ──────────────────────────────────────────────
  سایبرپانک: ['cyberpunk'],
  سینمایی: ['cinematic', 'cinema'],
  واقع‌گرایانه: ['realistic', 'photorealistic', 'realism'],
  رئالیستیک: ['realistic', 'photorealistic'],
  انتزاعی: ['abstract'],
  مینیمال: ['minimal', 'minimalist'],
  مینیمالیستی: ['minimalist', 'minimal'],
  وینتیج: ['vintage', 'retro'],
  رترو: ['retro'],
  قدیمی: ['vintage', 'old', 'retro'],
  فانتزی: ['fantasy'],
  'علمی تخیلی': ['sci-fi', 'science fiction'],
  تخیلی: ['fantasy', 'sci-fi'],
  گوتیک: ['gothic'],
  استیم‌پانک: ['steampunk'],
  سورئال: ['surreal', 'surrealism'],
  امپرسیونیست: ['impressionist', 'impressionism'],
  کارتونی: ['cartoon'],
  انیمه: ['anime'],
  مانگا: ['manga'],
  کمیک: ['comic'],
  پیکسلی: ['pixel art', 'pixel'],
  ایزومتریک: ['isometric'],
  نئون: ['neon'],
  پاستل: ['pastel'],
  مونوکروم: ['monochrome', 'black and white'],
  'سیاه و سفید': ['black and white', 'monochrome'],
  رنگارنگ: ['colorful', 'vibrant'],
  تاریک: ['dark', 'moody'],
  روشن: ['bright', 'light'],

  // ── subjects ───────────────────────────────────────────────────────────
  پرتره: ['portrait'],
  چهره: ['face', 'portrait'],
  منظره: ['landscape', 'scenery'],

  /**
   * Words a real user searched and got nothing for, on 2026-08-06.
   *
   * `سامورای` was typed three times in fourteen seconds — سامورایی, سامورا, سامورای — and returned
   * zero every time, while the library holds 113 samurai prompts. `کوهستان` returns zero against
   * 744 mountain prompts, `محصول` two against 1,547 product prompts. Nothing was broken: the words
   * simply were not here, and a term that is not in this table cannot match anything at all,
   * because the corpus is 100% English (zero of 20,266 prompts contain a Persian character).
   *
   * That is the argument for eventually indexing semantically — a table needs every word and a
   * person typing their own language does not consult it. Until then, the misses are ordinary
   * vocabulary and adding them costs nothing.
   */
  سامورایی: ['samurai'],
  سامورای: ['samurai'],
  نینجا: ['ninja'],
  کوهستان: ['mountain', 'mountains'],
  کوه: ['mountain'],
  محصول: ['product'],
  تبلیغاتی: ['advertising', 'commercial', 'advertisement'],
  جنگل: ['forest', 'woods'],
  دریا: ['sea', 'ocean'],
  اقیانوس: ['ocean'],
  رودخانه: ['river'],
  آبشار: ['waterfall'],
  بیابان: ['desert'],
  برف: ['snow', 'snowy'],
  باران: ['rain', 'rainy'],
  ابر: ['cloud', 'clouds'],
  غروب: ['sunset'],
  طلوع: ['sunrise'],
  ستاره: ['star', 'stars'],
  کهکشان: ['galaxy', 'space'],
  فضا: ['space'],
  سیاره: ['planet'],
  اژدها: ['dragon'],
  شوالیه: ['knight'],
  جادوگر: ['wizard', 'witch'],
  پری: ['fairy'],
  هیولا: ['monster', 'creature'],
  ربات: ['robot'],
  گربه: ['cat'],
  سگ: ['dog'],
  اسب: ['horse'],
  پرنده: ['bird'],
  گل: ['flower', 'floral'],
  درخت: ['tree'],
  غذا: ['food'],
  قهوه: ['coffee'],
  ماشین: ['car'],
  موتور: ['motorcycle'],
  هواپیما: ['airplane', 'plane'],
  ساختمان: ['building', 'architecture'],
  خانه: ['house', 'home'],
  اتاق: ['room', 'interior'],
  آشپزخانه: ['kitchen'],
  لباس: ['clothing', 'outfit', 'fashion'],
  کفش: ['shoes', 'sneakers'],
  جواهرات: ['jewelry'],
  عروسی: ['wedding'],
  کودک: ['child', 'kid'],
  مرد: ['man', 'male'],
  زن: ['woman', 'female'],
  مو: ['hair'],
  چشم: ['eyes', 'eye'],
  لبخند: ['smile', 'smiling'],
  کتاب: ['book'],
  نقشه: ['map'],
  پوستر: ['poster'],
  بنر: ['banner'],
  آیکون: ['icon'],
  نماد: ['symbol', 'emblem'],
  تایپوگرافی: ['typography'],
  /**
   * The union, not either version.
   *
   * The curated entry read `['font', 'typography']`; my duplicate read `['font', 'typeface']`, and
   * because a later key silently wins in an object literal, "typography" was dropped. Both English
   * words are things people search, so both stay.
   */
  فونت: ['font', 'typeface', 'typography'],
  الگو: ['pattern'],
  بافت: ['texture'],
  گرادیان: ['gradient'],
  سایه: ['shadow'],
  نور: ['light', 'lighting'],
  سیاه: ['black'],
  سفید: ['white'],
  طلایی: ['gold', 'golden'],
  نقره: ['silver'],
  قرمز: ['red'],
  آبی: ['blue'],
  سبز: ['green'],
  بنفش: ['purple'],
  نارنجی: ['orange'],
  صورتی: ['pink'],
  طبیعت: ['nature'],
  شهر: ['city', 'urban'],
  معماری: ['architecture'],
  داخلی: ['interior'],
  حیوان: ['animal'],
  خودرو: ['car', 'vehicle'],
  دختر: ['girl'],
  پسر: ['boy'],
  جنگجو: ['warrior'],
  شخصیت: ['character'],
  اواتار: ['avatar'],

  // ── design and product ────────────────────────────────────────────────
  لوگو: ['logo'],
  نشان: ['logo', 'emblem'],
  برند: ['brand', 'branding'],
  بروشور: ['brochure'],
  'کارت ویزیت': ['business card'],
  بسته‌بندی: ['packaging'],
  ایکون: ['icon'],
  'رابط کاربری': ['ui', 'user interface'],
  وب‌سایت: ['website', 'web'],
  اپلیکیشن: ['app', 'application'],
  موکاپ: ['mockup'],
  پس‌زمینه: ['background'],
  تصویرسازی: ['illustration'],
  گرافیک: ['graphic'],

  // ── photography and camera ────────────────────────────────────────────
  عکاسی: ['photography', 'photo'],
  عکس: ['photo', 'photograph'],
  دوربین: ['camera'],
  لنز: ['lens'],
  نورپردازی: ['lighting'],
  کنتراست: ['contrast'],
  فوکوس: ['focus'],
  بوکه: ['bokeh'],
  ماکرو: ['macro'],
  پانوراما: ['panorama'],
  زاویه: ['angle'],
  'نمای نزدیک': ['close-up', 'closeup'],
  'نمای باز': ['wide shot', 'wide'],
  شب: ['night'],
  روز: ['day', 'daylight'],

  // ── media and type ────────────────────────────────────────────────────
  ویدیو: ['video'],
  ویدئو: ['video'],
  فیلم: ['film', 'video', 'movie'],
  انیمیشن: ['animation', 'animated'],
  موسیقی: ['music'],
  آهنگ: ['song', 'music'],
  صدا: ['audio', 'sound'],
  'سه بعدی': ['3d'],
  سه‌بعدی: ['3d'],
  'دو بعدی': ['2d'],
  رندر: ['render', 'rendering'],
  مدل: ['model'],
  متن: ['text'],
  کد: ['code'],
  برنامه‌نویسی: ['programming', 'code', 'coding'],
  نوشتن: ['writing'],
  مقاله: ['article', 'writing'],
  ایمیل: ['email'],
  خلاصه: ['summary'],
  ترجمه: ['translation'],
  داستان: ['story'],
  شعر: ['poem', 'poetry'],
  بازاریابی: ['marketing'],
  // "advertisement" is not a superstring of "advertising", so 181 title matches needed it named.
  تبلیغات: ['ads', 'advertising', 'advertisement'],
  فروش: ['sales'],
  'کسب و کار': ['business'],
  دستیار: ['assistant', 'agent'],
  عامل: ['agent'],
  چت: ['chat'],

  // ── models and tools ──────────────────────────────────────────────────
  میدجرنی: ['midjourney'],
  میدجورنی: ['midjourney'],
  'استیبل دیفیوژن': ['stable diffusion'],
  دال‌ای: ['dall-e', 'dalle'],
  فلاکس: ['flux'],
  سورا: ['sora'],
  کلینگ: ['kling'],
  رانوی: ['runway'],
  سونو: ['suno'],
  جی‌پی‌تی: ['gpt', 'chatgpt'],
  کلاد: ['claude'],
  جمینای: ['gemini'],

  // ── qualities and modifiers ───────────────────────────────────────────
  باکیفیت: ['high quality', 'quality'],
  'کیفیت بالا': ['high quality'],
  جزئیات: ['detailed', 'detail'],
  دقیق: ['detailed', 'precise'],
  ساده: ['simple'],
  پیچیده: ['complex', 'intricate'],
  زیبا: ['beautiful'],
  حرفه‌ای: ['professional'],
  خلاقانه: ['creative'],
  مدرن: ['modern'],
  سنتی: ['traditional'],
  ایرانی: ['persian', 'iranian'],
  اسلامی: ['islamic'],
  ژاپنی: ['japanese'],
  چینی: ['chinese'],
  رنگ: ['color'],
  اندازه: ['size'],
  سبک: ['style'],
  طرح: ['design'],
  ایده: ['idea'],
  نمونه: ['example'],
  رایگان: ['free'],
  جدید: ['new'],
  محبوب: ['popular'],
  بهترین: ['best', 'top'],

  /**
   * ── Closed against the corpus, not against intuition (`infra/glossary-audit.mjs`) ──────
   *
   * Everything above this line was added because somebody thought of it. That is how a curated
   * table gets to 215 entries and still misses the second-largest category in the library.
   *
   * The audit asks the corpus instead. It reads every tag, category and model name the 20,266
   * published prompts actually carry, plus title words above a frequency floor, and reports the
   * ones no Persian key can reach — using the same substring semantics as `termMatches`, so
   * `story` counts as covering `storytelling` and nothing is added twice.
   *
   * It found 55 uncovered tags, categories and models. The ones below are those with a plausible
   * Persian search behind them, plus the frequent title words that name a *subject* rather than
   * English grammar. "featuring", "wearing", "uploaded" and "looking" are left out deliberately:
   * they are frequent because English sentences are, and a Persian key pointing at them would add
   * thousands of irrelevant rows to unrelated queries.
   *
   * Counts in the comments are prompts reachable, measured at the time of the audit.
   */

  // ── categories nobody could reach in Persian ──────────────────────────────
  'شبکه اجتماعی': ['social media'], //             303 prompts
  'سوشال مدیا': ['social media'],
  نقش‌آفرینی: ['roleplay', 'role play'], //      270
  پرسونا: ['persona', 'personas'],
  میم: ['meme', 'memes'], //                       253
  سرگرمی: ['fun', 'entertainment'],
  کدنویسی: ['coding', 'code'], //                  218 — برنامه‌نویسی gave 'programming', which
  // 'dev' is listed beside the long forms because neither contains it — the category is "coding
  // & dev", and `LIKE '%development%'` cannot match the three-letter word people actually type.
  توسعه: ['development', 'developer', 'dev'], //   does not contain "coding"
  فرانت‌اند: ['frontend', 'front-end'],
  داده: ['data'], //                                86
  تحلیل: ['analysis', 'analytics'],
  آموزش: ['education', 'tutorial', 'learning'], //  83
  یادگیری: ['learning', 'education'],

  // ── tags nobody could reach in Persian ────────────────────────────────────
  استودیو: ['studio', 'studio lighting'], //       818 — the largest single hole found
  'نور استودیویی': ['studio lighting'],
  کانسپت: ['concept', 'concept art'], //           403
  'هنر مفهومی': ['concept art'],
  پرامپت: ['prompt'], //                           396 — and the word a user typed for zero results
  وکتور: ['vector'],
  دودل: ['doodle'],
  چیبی: ['chibi'],
  ویپورویو: ['vaporwave'],
  'ساعت طلایی': ['golden hour'],
  'تاری حرکت': ['motion blur'],
  نوشیدنی: ['beverage', 'drink'],
  عطر: ['perfume', 'fragrance'],
  فوتبال: ['football', 'soccer'],
  ورزش: ['sport', 'sports'],
  دنج: ['cozy'],
  فنی: ['technical'],
  تابستان: ['summer'],
  زمستان: ['winter'],
  بهار: ['spring'],
  پاییز: ['autumn', 'fall'],

  // ── models ────────────────────────────────────────────────────────────────
  گراک: ['grok'],
  لئوناردو: ['leonardo'],
  ایمیجن: ['imagen'],
  پیکسار: ['pixar'],

  // ── frequent title subjects ───────────────────────────────────────────────
  ادیتوریال: ['editorial'], //                     756
  لوکس: ['luxury'], //                             451
  لاکچری: ['luxury'],
  نقاشی: ['painting', 'paint'], //                 431
  عمودی: ['vertical'], //                          404
  افقی: ['horizontal'],
  دیجیتال: ['digital'], //                         375
  صحنه: ['scene'], //                              369
  بامزه: ['cute'], //                              245
  ناز: ['cute'],
  سفر: ['travel'], //                              205
  گردشگری: ['travel', 'tourism'],
  کلاژ: ['collage'], //                            181
  آینده‌نگر: ['futuristic', 'future'], //          175
  فیوچریستیک: ['futuristic'],
  شاهکار: ['masterpiece'], //                      170
  اکشن: ['action'], //                             154
  بدن: ['body'], //                                154
  پویا: ['dynamic'], //                            153
  نما: ['view', 'shot'], //                        144
  مینیاتور: ['miniature'], //                      122
  ترکیب‌بندی: ['composition'], //                112
  کاور: ['cover'], //                              111
  درخشان: ['glowing', 'glow'], //                  107
  پریمیوم: ['premium'], //                         976
  اولترا: ['ultra'], //                            881
  هایپر: ['hyper'], //                             313
  رزولوشن: ['resolution'],

  /**
   * Second pass. Re-running the audit after the block above dropped uncovered tags/categories/
   * models from 55 to 26, and these are the remainder worth a Persian key. What is left after
   * this is `y2k` and `3d` — the first is already typed in Latin by everyone who wants it, and
   * the second is only listed because the audit ignores glossary terms under three characters
   * when testing coverage (`سه‌بعدی` does map to `3d`).
   */
  سیستم: ['system'], //                            396, with پرامپت, reaches system-prompt
  جیسون: ['json'], //                               40
  دواپس: ['devops'],
  ترکیبی: ['mixed media', 'mixed'], //              28
  اسپلش: ['splash'],
  فلاش: ['flash'],
  ووگ: ['vogue'],
  'نوردهی دوگانه': ['double exposure', 'double-exposure'],
  مجسمه: ['sculpture'],
  توکیو: ['tokyo'],
  سلفی: ['selfie'],
  آینه: ['mirror'],
  اینستاگرام: ['instagram'],
  'طراحی تخت': ['flat design', 'flat-design'],
  کافه: ['cafe', 'coffee shop'],
  ویو: ['veo'],
  کوئن: ['qwen'],
  کرسر: ['cursor'],
  سیدنس: ['seedance'],
  دستور: ['recipe', 'instruction'],
  مجله: ['magazine'],
  'هنر ناب': ['fine art', 'fine-art'],
};

/** Folded key → English terms, built once. */
const FOLDED: Map<string, string[]> = new Map(
  Object.entries(PAIRS).map(([fa, en]) => [foldPersian(fa), en]),
);

/** Every Persian term, for the admin glossary screen. */
export const GLOSSARY_PAIRS = PAIRS;
export const GLOSSARY_SIZE = Object.keys(PAIRS).length;

/** Single-word glossary keys, bucketed by length, so a near-match scan touches only candidates. */
const BY_LENGTH = new Map<number, string[]>();
for (const key of FOLDED.keys()) {
  if (key.includes(' ')) continue;
  const bucket = BY_LENGTH.get(key.length);
  if (bucket) bucket.push(key);
  else BY_LENGTH.set(key.length, [key]);
}

/**
 * Damerau-Levenshtein distance, bounded at one.
 *
 * A second copy of `services/fuzzy.ts`'s function would be the wrong instinct — except that one
 * lives in `apps/api` and this package must not depend on the Worker. This is the same algorithm
 * with the budget nailed to 1, which collapses it to a single scan: with at most one edit allowed,
 * the two strings can differ in at most one place, so walking both from the front and then from
 * the back answers it without a matrix.
 *
 * Transpositions count as one edit, for the same reason they do there: swapping two adjacent
 * letters is the commonest typo there is, and `سایبرپانک` mistyped as `سایبرانک` is exactly one
 * dropped letter.
 */
function withinOneEdit(a: string, b: string): boolean {
  if (a === b) return false; // an exact hit is not a correction
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  if (long.length - short.length > 1) return false;

  let head = 0;
  while (head < short.length && short[head] === long[head]) head++;
  let tail = 0;
  while (
    tail < short.length - head &&
    short[short.length - 1 - tail] === long[long.length - 1 - tail]
  ) {
    tail++;
  }
  const remaining = short.length - head - tail;
  if (remaining <= 0) return true; // one insertion, or identical prefixes/suffixes meeting
  if (remaining === 1 && short.length === long.length) return true; // one substitution
  // One transposition: the two disagreeing characters are each other's, swapped.
  return (
    remaining === 2 &&
    short.length === long.length &&
    short[head] === long[head + 1] &&
    short[head + 1] === long[head]
  );
}

/**
 * The Persian half of "did you mean".
 *
 * `services/fuzzy.ts` corrects misspellings against a vocabulary built from prompt titles, tags,
 * models and categories — **all of which are English**, because the corpus is. So a Persian typo
 * has nothing in that vocabulary to be corrected against and falls straight through to zero
 * results. The real query log shows it happening: `سایبرانک` is `سایبرپانک` with one letter
 * dropped, and it returned nothing while 549 cyberpunk prompts sat there.
 *
 * The glossary keys are the only Persian vocabulary this system has, so they are what a Persian
 * query gets corrected against. One edit, single words only, and a four-character floor — below
 * that a one-letter edit reaches too many unrelated words to be a guess worth making, and short
 * strings are usually somebody still typing rather than somebody who mistyped.
 */
const MIN_FUZZY = 4;
function nearestKey(token: string): string[] | undefined {
  if (token.length < MIN_FUZZY) return undefined;
  for (const length of [token.length, token.length - 1, token.length + 1]) {
    for (const key of BY_LENGTH.get(length) ?? []) {
      if (withinOneEdit(token, key)) return FOLDED.get(key);
    }
  }
  return undefined;
}

/**
 * English terms implied by a Persian query.
 *
 * Matches whole folded tokens and also multi-word entries («کسب و کار») by scanning the folded
 * query for each multi-word key. Returns an empty array for a query with nothing Persian in it, so
 * callers can skip the expansion branch entirely.
 */
export function expandPersianQuery(query: string): string[] {
  const folded = foldPersian(query).toLowerCase();
  if (!folded) return [];

  const out = new Set<string>();

  // Multi-word entries first: they contain spaces, so a token scan would never see them.
  for (const [key, terms] of FOLDED) {
    if (key.includes(' ') && folded.includes(key)) terms.forEach((t) => out.add(t));
  }
  const unmatched: string[] = [];
  for (const token of folded.split(/[^\p{L}\p{N}]+/u)) {
    const terms = FOLDED.get(token);
    if (terms) terms.forEach((t) => out.add(t));
    else if (token) unmatched.push(token);
  }

  /**
   * Only when nothing matched exactly.
   *
   * A query like `پرتره سینمایی` has two solid hits and does not want a guess bolted on; a query
   * that hit nothing has nowhere else to go. Deferring the fuzzy pass to the all-miss case also
   * keeps it off the hot path — most Persian queries never reach it.
   */
  if (!out.size) {
    for (const token of unmatched) nearestKey(token)?.forEach((t) => out.add(t));
  }
  return [...out];
}
